import React, { useEffect, useState } from "react";
import { getPageContent, updatePageContent } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/PagesManager.css";

const PAGE_OPTIONS = [
  { key: "contact", label: "Contact" },
  { key: "donations", label: "Donations" },
  { key: "homepage", label: "Homepage" },
  { key: "members", label: "Members" },
  { key: "open-source", label: "Open Source" },
  { key: "partners", label: "Partners" },
  { key: "publications", label: "Publications" },
  { key: "research", label: "Research" },
  { key: "webinars", label: "Webinars" },
];

const emptyForm = {
  en: { meta_title: "", title: "", description: "" },
  vi: { meta_title: "", title: "", description: "" },
};

function mapRowsToForm(rows) {
  const form = JSON.parse(JSON.stringify(emptyForm));
  if (!Array.isArray(rows)) {
    return form;
  }

  rows.forEach((row) => {
    const lang = row.language || "";
    if (lang === "en" || lang === "vi") {
      form[lang] = {
        meta_title: row.meta_title || "",
        title: row.title || "",
        description: row.description || "",
      };
    }
  });

  return form;
}

const PagesManager = () => {
  const [activePage, setActivePage] = useState("members");
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "success" });

  useEffect(() => {
    fetchPage();
  }, [activePage]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const rows = await getPageContent(activePage);
      setFormData(mapRowsToForm(rows));
    } catch (err) {
      setToast({ message: err.message || "Failed to load page content", type: "error" });
      setFormData(emptyForm);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updatePageContent(activePage, formData);
      setToast({ message: "Saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save page content", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-manager-loading">Loading page content...</div>;
  }

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="page-selector">
        <label htmlFor="page-select">Select Page</label>
        <select id="page-select" value={activePage} onChange={(e) => setActivePage(e.target.value)}>
          {PAGE_OPTIONS.map((page) => (
            <option key={page.key} value={page.key}>
              {page.label}
            </option>
          ))}
        </select>
      </div>

      <div className="admin-manager-form">
        <h3>Page Content</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h4>English</h4>
            <div className="form-group">
              <label>Meta Title</label>
              <input
                type="text"
                value={formData.en.meta_title}
                onChange={(e) => handleInputChange("en", "meta_title", e.target.value)}
                placeholder="Meta title"
              />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={formData.en.title}
                onChange={(e) => handleInputChange("en", "title", e.target.value)}
                placeholder="Page title"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.en.description}
                onChange={(e) => handleInputChange("en", "description", e.target.value)}
                placeholder="Page description"
              />
            </div>
          </div>

          <div className="form-section">
            <h4>Vietnamese</h4>
            <div className="form-group">
              <label>Meta Title</label>
              <input
                type="text"
                value={formData.vi.meta_title}
                onChange={(e) => handleInputChange("vi", "meta_title", e.target.value)}
                placeholder="Tiêu đề meta"
              />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={formData.vi.title}
                onChange={(e) => handleInputChange("vi", "title", e.target.value)}
                placeholder="Tiêu đề trang"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.vi.description}
                onChange={(e) => handleInputChange("vi", "description", e.target.value)}
                placeholder="Mô tả trang"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PagesManager;
